import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ArrowsOutSimple, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowClockwise } from '@phosphor-icons/react';

/**
 * ImageCropDialog
 *
 * Simple canvas-based crop editor: user picks scale + offset of the source image
 * inside a fixed output box. Returns a File (PNG) via onConfirm.
 *
 * Props:
 *  - open, onOpenChange
 *  - file: File | null      (source file to crop)
 *  - aspect: 'square' | 'wide' | 'landscape'
 *  - title: string
 *  - outputSize: number     (px, for the square/wide max dimension)
 *  - onConfirm(file: File)
 */
export default function ImageCropDialog({
  open,
  onOpenChange,
  file,
  aspect = 'square',
  title = 'Ajustar imagen',
  outputSize = 512,
  onConfirm,
}) {
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null); // HTMLImageElement
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [drag, setDrag] = useState(null);
  const [working, setWorking] = useState(false);

  // Output box dimensions (aspect ratio)
  const outW = aspect === 'wide' ? outputSize * 2 : (aspect === 'landscape' ? outputSize * 1.6 : outputSize);
  const outH = aspect === 'wide' ? outputSize : (aspect === 'landscape' ? outputSize : outputSize);
  const previewScale = Math.min(360 / outW, 260 / outH, 1);
  const canvasW = Math.round(outW * previewScale);
  const canvasH = Math.round(outH * previewScale);

  // Load the image when file changes
  useEffect(() => {
    if (!file) { setImg(null); return; }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setImg(image);
      // Fit-to-cover initial scale
      const s = Math.max(canvasW / image.width, canvasH / image.height);
      setScale(s);
      setOffsetX(0);
      setOffsetY(0);
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      toast.error('No se pudo leer la imagen');
      URL.revokeObjectURL(url);
    };
    image.src = url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, aspect]);

  // Draw preview on canvas whenever state changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');
    // Checker pattern
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvasW, canvasH);
    // Center image based on offset + scale
    const iw = img.width * scale;
    const ih = img.height * scale;
    const cx = (canvasW - iw) / 2 + offsetX;
    const cy = (canvasH - ih) / 2 + offsetY;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, cx, cy, iw, ih);
    // Frame
    ctx.strokeStyle = '#0891b2';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, canvasW, canvasH);
  }, [img, scale, offsetX, offsetY, canvasW, canvasH]);

  const onMouseDown = (e) => {
    if (!img) return;
    setDrag({ x: e.clientX, y: e.clientY, ox: offsetX, oy: offsetY });
  };
  const onMouseMove = (e) => {
    if (!drag) return;
    setOffsetX(drag.ox + (e.clientX - drag.x));
    setOffsetY(drag.oy + (e.clientY - drag.y));
  };
  const onMouseUp = () => setDrag(null);

  const zoom = (delta) => setScale(s => Math.max(0.05, Math.min(10, s * delta)));
  const reset = () => {
    if (!img) return;
    const s = Math.max(canvasW / img.width, canvasH / img.height);
    setScale(s);
    setOffsetX(0);
    setOffsetY(0);
  };

  const handleConfirm = async () => {
    if (!img) { onOpenChange(false); return; }
    setWorking(true);
    try {
      // Render at full output size (not preview scale)
      const output = document.createElement('canvas');
      output.width = outW;
      output.height = outH;
      const octx = output.getContext('2d');
      octx.fillStyle = '#ffffff';
      octx.fillRect(0, 0, outW, outH);
      const fullScale = scale / previewScale;
      const iw = img.width * fullScale;
      const ih = img.height * fullScale;
      const cx = (outW - iw) / 2 + (offsetX / previewScale);
      const cy = (outH - ih) / 2 + (offsetY / previewScale);
      octx.imageSmoothingQuality = 'high';
      octx.drawImage(img, cx, cy, iw, ih);

      const blob = await new Promise(res => output.toBlob(res, 'image/png', 0.92));
      if (!blob) throw new Error('No se pudo generar la imagen');
      const filename = (file?.name || 'logo').replace(/\.[^.]+$/, '') + '.png';
      const outFile = new File([blob], filename, { type: 'image/png' });
      onConfirm?.(outFile);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Error al recortar');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Arrastra para reposicionar y usa los botones para acercar o alejar. Al guardar se subirá la versión recortada.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3">
          <div
            className="rounded-lg overflow-hidden bg-slate-50 border border-slate-200 select-none"
            style={{ width: canvasW, height: canvasH, cursor: drag ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            <canvas ref={canvasRef} width={canvasW} height={canvasH} />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => zoom(0.85)} data-testid="crop-zoom-out">
              <MagnifyingGlassMinus size={14} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => zoom(1.15)} data-testid="crop-zoom-in">
              <MagnifyingGlassPlus size={14} />
            </Button>
            <Button variant="outline" size="sm" onClick={reset} data-testid="crop-reset">
              <ArrowClockwise size={14} className="mr-1" />
              Ajustar
            </Button>
          </div>
          <div className="text-[11px] text-slate-500">Salida: {outW} × {outH} px · PNG</div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={working}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={working || !img} data-testid="crop-confirm">
            <ArrowsOutSimple size={14} className="mr-1" />
            {working ? 'Procesando…' : 'Aplicar y subir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
